/**
 * AppLayout: AppBar + persistent Drawer + Outlet. Rendered inside the
 * AuthGate (so it only mounts when authenticated).
 *
 * Sections mirror docs/design §3.1. The drawer is persistent on md+; on
 * smaller widths it becomes a temporary drawer toggled by the hamburger.
 */
import {
  AppBar,
  Box,
  Chip,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import PaletteIcon from '@mui/icons-material/Palette';
import PeopleIcon from '@mui/icons-material/People';
import InventoryIcon from '@mui/icons-material/Inventory2';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SecurityIcon from '@mui/icons-material/Security';
import BuildIcon from '@mui/icons-material/Build';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import { useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { LanguageSwitcher } from './LanguageSwitcher';
import { CommandPalette } from './CommandPalette';
import { useCopy } from '@/hooks/useCopy';
import { useCurrentOperator, useAuth } from '@/auth/AuthContext';
import { authApi } from '@/api/auth';
import { clearDeepLink } from '@/auth/silentRefresh';
import { env } from '@/config/env';

const DRAWER_WIDTH = 240;

interface NavItem {
  to: string;
  labelKey: string;
  fallback: string;
  icon: ReactNode;
  exact?: boolean;
  superAdmin?: boolean;
}

const NAV: NavItem[] = [
  { to: '/', labelKey: 'nav.home', fallback: 'Home', icon: <HomeIcon />, exact: true },
  {
    to: '/reference/products',
    labelKey: 'nav.products',
    fallback: 'Products',
    icon: <InventoryIcon />,
  },
  { to: '/tenants', labelKey: 'nav.tenants', fallback: 'Tenants', icon: <BusinessIcon /> },
  { to: '/brands', labelKey: 'nav.brands', fallback: 'Brands', icon: <PaletteIcon /> },
  {
    to: '/reference/oidc-clients',
    labelKey: 'nav.oidcClients',
    fallback: 'OIDC clients',
    icon: <VpnKeyIcon />,
  },
  { to: '/users', labelKey: 'nav.users', fallback: 'Users', icon: <PeopleIcon /> },
  {
    to: '/reference/operators',
    labelKey: 'nav.operators',
    fallback: 'Operators',
    icon: <SecurityIcon />,
    superAdmin: true,
  },
  {
    to: '/operations',
    labelKey: 'nav.operations',
    fallback: 'Operations',
    icon: <BuildIcon />,
  },
];

export function AppLayout() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { t } = useCopy();
  const operator = useCurrentOperator();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Non-prod banner (SASADM-0080): tint the AppBar + show an env chip so staging/
  // local builds are unmistakable. Production renders the shell unchanged.
  const isNonProd = env.envLabel !== 'production';

  const handleSignOut = async () => {
    try {
      await authApi.logout();
    } catch {
      /* logout errors are not blocking */
    }
    clearDeepLink();
    auth.signOut();
    navigate('/auth/signed-out', { replace: true });
  };

  const drawerContent = (
    <Box sx={{ width: DRAWER_WIDTH, display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* The Drawer renders a single <Toolbar /> spacer (AppBar offset) above this;
          nav items sit flush right below it — no extra top gap. */}
      <List>
        {NAV.filter((item) => !item.superAdmin || operator?.isSuperAdmin).map((item) => (
          <NavLinkItem key={item.to} item={item} onClick={() => setMobileOpen(false)} t={t} />
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        color="default"
        elevation={0}
        sx={{
          borderBottom: isNonProd ? 3 : 1,
          borderColor: isNonProd ? 'warning.main' : 'divider',
          zIndex: (th) => th.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          {!isDesktop ? (
            <IconButton
              edge="start"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={t('common.menu', 'Menu')}
            >
              <MenuIcon />
            </IconButton>
          ) : null}
          <Typography
            variant="h1"
            component={NavLink}
            to="/"
            sx={{
              fontSize: '1.05rem',
              ml: isDesktop ? 0 : 1,
              textDecoration: 'none',
              color: 'inherit',
              fontWeight: 600,
            }}
          >
            {t('app.title', 'Journix SaaS Platform Admin')}
          </Typography>
          {isNonProd ? (
            <Chip
              label={env.envLabel.toUpperCase()}
              color="warning"
              size="small"
              sx={{ ml: 1.5, fontWeight: 700, letterSpacing: 0.5 }}
            />
          ) : null}
          <Box sx={{ flex: 1 }} />
          <LanguageSwitcher />
          <Tooltip title={operator?.email ?? t('operator.menuTitle', 'Account')}>
            <IconButton
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              aria-label={t('operator.menuTitle', 'Account')}
              sx={{ ml: 1 }}
            >
              <AccountCircleIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {operator ? (
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="body2">{operator.name ?? operator.email}</Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {operator.email}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={t(
                      `operator.role.${operator.role}`,
                      operator.role === 'SUPER_ADMIN' ? 'Super admin' : 'Admin',
                    )}
                    size="small"
                    color={operator.isSuperAdmin ? 'primary' : 'default'}
                  />
                </Box>
              </Box>
            ) : null}
            <Divider />
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                void handleSignOut();
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('nav.signOut', 'Sign out')}</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Drawer
        variant={isDesktop ? 'permanent' : 'temporary'}
        open={isDesktop ? true : mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: DRAWER_WIDTH,
          [`& .MuiDrawer-paper`]: {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar />
        {drawerContent}
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: 0 },
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <Toolbar />
        <Container maxWidth="xl" sx={{ py: 4 }} key={location.pathname.split('/')[1] ?? 'root'}>
          <Outlet />
        </Container>
      </Box>
      <CommandPalette />
    </Box>
  );
}

interface NavLinkItemProps {
  item: NavItem;
  onClick: () => void;
  t: (key: string, fallback?: string) => string;
}

function NavLinkItem({ item, onClick, t }: NavLinkItemProps) {
  return (
    <ListItemButton
      component={NavLink}
      to={item.to}
      end={item.exact ?? false}
      onClick={onClick}
      sx={{
        '&.active': {
          bgcolor: 'action.selected',
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
      <ListItemText primary={t(item.labelKey, item.fallback)} />
    </ListItemButton>
  );
}
